import { Component, ReactNode, ErrorInfo } from 'react'
import * as Sentry from '@sentry/react'
import { AlertCircle, RefreshCw } from 'lucide-react'
import { Button } from './ui'

interface Props {
  children: ReactNode
  fallback?: (error: Error, reset: () => void) => ReactNode
  showDetails?: boolean
  section?: string
}

interface State {
  hasError: boolean
  error: Error | null
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props)
    this.state = { hasError: false, error: null }
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    const { section = 'unknown' } = this.props
    
    // Log to Sentry with additional context
    Sentry.withScope((scope) => {
      scope.setTag('error_boundary_section', section)
      scope.setContext('component_stack', {
        componentStack: errorInfo.componentStack
      })
      scope.setLevel('error')
      Sentry.captureException(error)
    })

    // Add breadcrumb for error boundary catch
    Sentry.addBreadcrumb({
      category: 'error_boundary',
      message: `Error caught in ${section} section`,
      level: 'error',
      data: {
        section,
        errorMessage: error.message,
      }
    })
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null })
  }

  render() {
    if (this.state.hasError && this.state.error) {
      const { fallback, showDetails = true } = this.props

      if (fallback) {
        return fallback(this.state.error, this.handleReset)
      }

      return (
        <div className="min-h-[200px] flex items-center justify-center p-4">
          <div className="max-w-md w-full bg-white border border-red-200 rounded-lg shadow-sm p-6">
            <div className="flex items-start space-x-3">
              <AlertCircle className="w-6 h-6 text-red-500 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-red-800">
                  Something went wrong
                </h3>
                <p className="mt-1 text-sm text-red-600">
                  An error occurred in this section. You can try refreshing or continue using other parts of the application.
                </p>
                
                {showDetails && (
                  <details className="mt-3">
                    <summary className="cursor-pointer text-sm text-gray-600 hover:text-gray-800">
                      Show error details
                    </summary>
                    <pre className="mt-2 text-xs bg-gray-100 p-2 rounded overflow-x-auto text-gray-700">
                      {this.state.error.stack || this.state.error.message}
                    </pre>
                  </details>
                )}
                
                <div className="mt-4 flex space-x-3">
                  <Button
                    size="sm"
                    variant="primary"
                    onClick={this.handleReset}
                    className="flex items-center gap-2"
                  >
                    <RefreshCw className="w-4 h-4" />
                    Try Again
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => window.location.reload()}
                  >
                    Refresh Page
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )
    }

    return this.props.children
  }
}

// Functional component wrapper for easier use with hooks
export const SentryErrorBoundary = Sentry.ErrorBoundary